import { groupThousands } from './group';

/**
 * How a figure is rounded to the places it shows. `half-up` to read,
 * `down` for a cap that must never read higher than it is (what can be
 * withdrawn), `up` for a bound that must never read lower (the most a cost
 * can be).
 */
export type UnitsRounding = 'half-up' | 'down' | 'up';

/**
 * `raw / 10^decimals` at `places` decimal places, as an integer, rounded
 * half up unless `round` says otherwise. `raw` is never negative here.
 */
export function roundToPlaces(
  raw: bigint,
  decimals: number,
  places: number,
  round: UnitsRounding = 'half-up'
): bigint {
  if (decimals <= places) return raw * 10n ** BigInt(places - decimals);
  const unit = 10n ** BigInt(decimals - places);
  const carry = round === 'down' ? 0n : round === 'up' ? unit - 1n : unit / 2n;
  return (raw + carry) / unit;
}

/** An integer at `places` places, grouped, trailing zeros dropped down to `minFraction`. */
export function placedDecimal(
  scaled: bigint,
  places: number,
  minFraction: number
): string {
  const unit = 10n ** BigInt(places);
  const whole = groupThousands((scaled / unit).toString());
  if (places === 0) return whole;
  const fraction = (scaled % unit)
    .toString()
    .padStart(places, '0')
    .replace(/0+$/, '')
    .padEnd(minFraction, '0');
  return fraction ? `${whole}.${fraction}` : whole;
}
